import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Phone, Mail, Clock, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

// Instagram ve YouTube SVG ikonları
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YouTubeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: '',
    kvkkConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  // Telefon numarası için sadece rakam ve formatlamaya izin ver
  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/[^0-9+\s]/g, '');
    // Maksimum 15 karakter (uluslararası format için)
    if (value.length <= 17) {
      setFormData({ ...formData, phone: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.kvkkConsent) {
      toast.error('Lutfen KVKK ve Gizlilik Politikasini kabul edin.');
      return;
    }
    
    setIsSubmitting(true);

    const serviceLine = formData.service ? `Hizmet: ${formData.service}` : 'Hizmet: Belirtilmedi';
    const budgetLine = formData.budget ? `Butce: ${formData.budget}` : 'Butce: Belirtilmedi';

    const whatsappMessage = [
      'Yeni Buyume Analizi Talebi',
      '',
      `Isim: ${formData.name}`,
      `E-posta: ${formData.email}`,
      `Telefon: ${formData.phone}`,
      `Sirket: ${formData.company || 'Belirtilmedi'}`,
      serviceLine,
      budgetLine,
      '',
      'Mesaj:',
      formData.message,
    ].join('\n');

    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/905321686282?text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank');

    setIsSubmitting(false);
    setShowThankYou(true);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 sm:pt-32" data-testid="contact-page">
      <div className="container-wide section-padding">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Side - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs font-mono text-violet-400 tracking-widest uppercase mb-4 block">
              İletişim
            </span>
            <h1 className="heading-section text-white mb-6">
              Büyüme Yolculuğunuza
              <br />
              <span className="gradient-text-refined">Başlayın</span>
            </h1>
            <p className="text-zinc-400 text-base sm:text-lg mb-10 leading-relaxed">
              Ücretsiz büyüme analizi ile dijital varlığınızın potansiyelini keşfedin. 
              30 dakikalık görüşmemizde, işinize özel strateji önerileri sunuyoruz.
            </p>

            {/* Contact Info */}
            <div className="space-y-5 mb-10">
              <a 
                href="tel:+905321686282" 
                className="flex items-center gap-4 text-zinc-400 hover:text-white transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#121212] border border-[#27272A] flex items-center justify-center group-hover:border-violet-500/50 transition-colors flex-shrink-0">
                  <Phone className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Telefon</p>
                  <p className="text-white font-medium">+90 532 168 62 82</p>
                </div>
              </a>

              <a 
                href="mailto:info@medyatik.app" 
                className="flex items-center gap-4 text-zinc-400 hover:text-white transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#121212] border border-[#27272A] flex items-center justify-center group-hover:border-violet-500/50 transition-colors flex-shrink-0">
                  <Mail className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">E-posta</p>
                  <p className="text-white font-medium text-sm sm:text-base">info@medyatik.app</p>
                </div>
              </a>

              <a 
                href="https://maps.google.com/?q=Esentepe+Mahallesi+Talatpaşa+Caddesi+No:5/1+Şişli+İstanbul"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 text-zinc-400 hover:text-white transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#121212] border border-[#27272A] flex items-center justify-center group-hover:border-violet-500/50 transition-colors flex-shrink-0">
                  <MapPin className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Konum</p>
                  <p className="text-white font-medium text-sm sm:text-base leading-relaxed">
                    Esentepe Mah. Talatpaşa Cad.<br />
                    No: 5/1, 34394 Şişli/İstanbul
                  </p>
                </div>
              </a>
            </div>

            {/* Social Media */}
            <div className="flex items-center gap-4 mb-8">
              <a 
                href="https://www.instagram.com/medyatikinteractive/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-xl bg-[#121212] border border-[#27272A] flex items-center justify-center text-zinc-400 hover:text-white hover:border-pink-500/50 hover:bg-pink-500/10 transition-all"
                aria-label="Instagram"
              >
                <InstagramIcon className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@medyatikinteractive8881"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-xl bg-[#121212] border border-[#27272A] flex items-center justify-center text-zinc-400 hover:text-white hover:border-red-500/50 hover:bg-red-500/10 transition-all"
                aria-label="YouTube"
              >
                <YouTubeIcon className="w-5 h-5" />
              </a>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 sm:p-8 md:p-10">
              {showThankYou ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Talebiniz Al{'\u0131'}nd{'\u0131'}</h3>
                  <p className="text-zinc-400 leading-relaxed max-w-md mx-auto mb-3">
                    B{'\u00fc'}y{'\u00fc'}me yolculu{'\u011f'}unuzda ilk ad{'\u0131'}m{'\u0131'} att{'\u0131'}n{'\u0131'}z. Ekibimiz talebinizi inceliyor ve sizin i{'\u00e7'}in {'\u00f6'}zel bir g{'\u00f6'}r{'\u00fc'}{'\u015f'}me planl{'\u0131'}yor.
                  </p>
                  <p className="text-zinc-300 font-medium mb-8">
                    En ge{'\u00e7'} 30 dakika i{'\u00e7'}inde sizinle ileti{'\u015f'}ime ge{'\u00e7'}ece{'\u011f'}iz.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowThankYou(false);
                      setFormData({ name: '', email: '', phone: '', company: '', service: '', budget: '', message: '', kvkkConsent: false });
                    }}
                    className="text-sm text-violet-400 hover:text-violet-300 transition-colors underline underline-offset-4"
                  >
                    Yeni talep olu{'\u015f'}tur
                  </button>
                </motion.div>
              ) : (
              <>
              <h3 className="text-xl font-bold text-white mb-6">Büyüme Analizi Talep Et</h3>
              
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm text-zinc-400 mb-2">
                      İsim Soyisim *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="contact-input"
                      placeholder="Adınız"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-zinc-400 mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="contact-input"
                      placeholder="ornek@email.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm text-zinc-400 mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      required
                      inputMode="numeric"
                      pattern="[0-9+\s]*"
                      className="contact-input"
                      placeholder="+90 5XX XXX XX XX"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm text-zinc-400 mb-2">
                      Şirket
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="contact-input"
                      placeholder="Şirket adı"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label htmlFor="service" className="block text-sm text-zinc-400 mb-2">
                      Hangi Hizmetle İlgileniyorsunuz? *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="contact-input appearance-none"
                      data-testid="input-service"
                    >
                      <option value="" disabled>Seçiniz</option>
                      <option value="Komple Büyüme Sistemi">Komple Büyüme Sistemi</option>
                      <option value="Web Tasarım & Geliştirme">Web Tasarım & Geliştirme</option>
                      <option value="Dijital Reklam Yönetimi">Dijital Reklam Yönetimi</option>
                      <option value="İçerik & Sosyal Medya">İçerik & Sosyal Medya</option>
                      <option value="SEO & Organik Büyüme">SEO & Organik Büyüme</option>
                      <option value="AI Destekli Pazarlama">AI Destekli Pazarlama</option>
                      <option value="Danışmanlık">Danışmanlık</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="budget" className="block text-sm text-zinc-400 mb-2">
                      Aylık Bütçe Aralığınız
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="contact-input appearance-none"
                      data-testid="input-budget"
                    >
                      <option value="" disabled>Seçiniz</option>
                      <option value="10.000₺ - 25.000₺">10.000₺ - 25.000₺</option>
                      <option value="25.000₺ - 50.000₺">25.000₺ - 50.000₺</option>
                      <option value="50.000₺ - 100.000₺">50.000₺ - 100.000₺</option>
                      <option value="100.000₺+">100.000₺+</option>
                      <option value="Henüz belirlemedim">Henüz belirlemedim</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm text-zinc-400 mb-2">
                    Mesajınız *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="contact-input resize-none"
                    placeholder="Projeniz hakkında kısaca bilgi verin..."
                    data-testid="input-message"
                  />
                </div>

                {/* KVKK Consent Checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="kvkkConsent"
                    name="kvkkConsent"
                    checked={formData.kvkkConsent}
                    onChange={handleChange}
                    required
                    className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-violet-500 focus:ring-violet-500 focus:ring-offset-0"
                    data-testid="kvkk-checkbox"
                  />
                  <label htmlFor="kvkkConsent" className="text-sm text-zinc-400 leading-relaxed">
                    <Link to="/kvkk" className="text-violet-400 hover:underline">KVKK Aydınlatma Metni</Link>'ni ve{' '}
                    <Link to="/gizlilik" className="text-violet-400 hover:underline">Gizlilik Politikası</Link>'nı okudum, kabul ediyorum. *
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.kvkkConsent}
                  className="w-full btn-gradient-refined flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      WhatsApp ile Gönder
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>

                <div className="flex items-center gap-3 mt-4 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-emerald-400" />
                  </div>
                  <p className="text-sm text-zinc-400">
                    <span className="text-emerald-400 font-medium">30 dakika içinde</span> dönüş garantisi
                  </p>
                </div>
              </form>
              </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
